import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

import {PasswordRules} from 'app/core/auth/password-policy.service';

/** One requirement, and whether the password currently meets it. */
export interface PasswordRuleState {
    key: string;
    label: string;
    met: boolean;
}

/**
 * Whether a character satisfies the "special character" rule: anything that is not a letter or a
 * digit. Whitespace is allowed in a password but does not satisfy the rule on its own.
 * <p>
 * This mirrors PasswordPolicyService.isSpecialCharacter on the API. The two must agree, or the form
 * accepts a password the server then refuses.
 */
function isSpecialCharacter(character: string): boolean {
    return !/[\p{L}\p{N}]/u.test(character) && !/\s/u.test(character);
}

/**
 * Evaluates a password against a tenant's rules, returning every requirement with its state so a
 * form can show the whole checklist rather than one failure at a time.
 */
export function evaluatePasswordRules(password: string, rules: PasswordRules): PasswordRuleState[] {
    const value = password ?? '';
    const states: PasswordRuleState[] = [
        {
            key: 'minLength',
            label: `At least ${rules.minLength} characters`,
            met: value.length >= rules.minLength
        }
    ];

    if (rules.requireUppercase) {
        states.push({key: 'uppercase', label: 'An uppercase letter', met: /\p{Lu}/u.test(value)});
    }
    if (rules.requireLowercase) {
        states.push({key: 'lowercase', label: 'A lowercase letter', met: /\p{Ll}/u.test(value)});
    }
    if (rules.requireNumber) {
        states.push({key: 'number', label: 'A number', met: /\p{N}/u.test(value)});
    }
    if (rules.requireSpecialCharacter) {
        states.push({
            key: 'specialCharacter',
            label: 'A special character',
            met: Array.from(value).some(isSpecialCharacter)
        });
    }

    return states;
}

/**
 * A validator bound to a tenant's rules. The rules arrive asynchronously, so the caller updates the
 * holder and re-runs validation once they land.
 */
export function passwordRulesValidator(rulesProvider: () => PasswordRules): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const rules = rulesProvider();
        if (!rules) {
            return null;
        }

        const value = control.value ?? '';
        if (!value) {
            // An empty field is "required"'s business, not ours.
            return null;
        }

        if (value.length > rules.maxLength) {
            return {passwordTooLong: {maxLength: rules.maxLength}};
        }

        const unmet = evaluatePasswordRules(value, rules).filter((rule) => !rule.met);
        return unmet.length ? {passwordRulesUnmet: {unmet: unmet.map((rule) => rule.key)}} : null;
    };
}
